import { TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import CheckIconAlt from '@src/svgs/CheckIconAlt';
import CommentsIcon from '@src/svgs/CommentsIcon';
import DeleteIcon from '@src/svgs/DeleteIcon';
import PencilIcon from '@src/svgs/PencilIcon';
import { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { formatDate } from './formatDate';
import { DangerButton, FormActions, GhostButton } from './formControls';

import type { TranslationMemoryEntry } from '@src/repositories/TranslationMemoryEntryRepository';

interface Props {
  entry: TranslationMemoryEntry;
  /** Number of table columns, so the delete-confirm row can span the full width. */
  columnCount: number;
  /** Collection name to show in the "All translations" view; undefined hides
   *  the column (a single-collection view doesn't need it). */
  collectionName?: string;
  /** Whether this row is part of the bulk selection (PLAT-10873). */
  selected: boolean;
  /** Toggle this row's membership in the bulk selection. */
  onToggleSelected: () => void;
  onEdit: () => void;
  /** Resolve `null` on success or an error message to surface inline. */
  onDelete: () => Promise<string | null>;
}

const ORIGIN_LABELS: Record<string, string> = {
  manual: 'Manual',
  import: 'Import',
  promoted: 'Promoted',
};

// Renders the `<td>` cells for one entry. The wrapping `<tr>` is supplied by
// the caller (TableVirtuoso's `TableRow` = the exported `EntryTr`), so this
// returns a cell fragment (or, in confirm mode, a single full-width cell).
export const EntryRowCells = ({
  entry,
  columnCount,
  collectionName,
  selected,
  onToggleSelected,
  onEdit,
  onDelete,
}: Props) => {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDnt = entry.translation === entry.sourceTerm;

  const confirmDelete = async () => {
    setBusy(true);
    setError(null);
    const message = await onDelete();
    setBusy(false);
    if (message) setError(message);
  };

  if (confirming) {
    return (
      <ConfirmCell colSpan={columnCount}>
        <ConfirmBox role="alertdialog">
          <p>
            Delete <strong>{entry.sourceTerm}</strong>? This removes the term
            rule permanently.
          </p>
          {error && <ErrorText role="alert">{error}</ErrorText>}
          <FormActions>
            <GhostButton
              type="button"
              onClick={() => {
                setError(null);
                setConfirming(false);
              }}
              disabled={busy}
            >
              Cancel
            </GhostButton>
            <DangerButton type="button" onClick={confirmDelete} disabled={busy}>
              Delete
            </DangerButton>
          </FormActions>
        </ConfirmBox>
      </ConfirmCell>
    );
  }

  const comment = entry.comment?.trim();
  const commentTooltipId = `tm-comment-${entry.id}`;

  return (
    <>
      <td className="select">
        <SelectCheckbox
          checked={selected}
          onChange={onToggleSelected}
          aria-label={`Select ${entry.sourceTerm}`}
        />
      </td>
      <td className="term">{entry.sourceTerm}</td>
      <td className="translation">
        <TranslationCell>
          {/* Fixed-width gutter so the translation text lines up across rows
              whether or not the entry has a comment. */}
          <CommentSlot>
            {comment && (
              <>
                <CommentButton
                  type="button"
                  aria-label="View comment"
                  data-tooltip-id={commentTooltipId}
                  data-tooltip-content={comment}
                >
                  <CommentsIcon
                    width="16"
                    height="16"
                    strokeColor="currentColor"
                  />
                </CommentButton>
                <Tooltip
                  id={commentTooltipId}
                  style={COMMENT_TOOLTIP_STYLE}
                  place="top"
                />
              </>
            )}
          </CommentSlot>
          <span className="value">
            {isDnt ? (
              <DntBadge>Do not translate</DntBadge>
            ) : (
              entry.translation || '—'
            )}
          </span>
        </TranslationCell>
      </td>
      {collectionName != null && (
        <td className="collection">{collectionName}</td>
      )}
      <td className="lang">{entry.sourceLanguage?.caption ?? '—'}</td>
      <td className="lang">
        {entry.translationLanguage?.caption ?? 'All languages'}
      </td>
      <td className="meta">
        <MetaStack>
          <OriginBadge>
            {ORIGIN_LABELS[entry.origin] ?? entry.origin}
          </OriginBadge>
          <span className="date">
            {formatDate(entry.updatedAt ?? entry.createdAt)}
          </span>
        </MetaStack>
      </td>
      <td className="actions">
        <Actions>
          <IconButton type="button" aria-label="Edit entry" onClick={onEdit}>
            <PencilIcon width="13" height="14" strokeColor="currentColor" />
          </IconButton>
          <IconButton
            type="button"
            aria-label="Delete entry"
            onClick={() => setConfirming(true)}
          >
            <DeleteIcon width="14" height="14" strokeColor="currentColor" />
          </IconButton>
        </Actions>
      </td>
    </>
  );
};

// Long comments wrap instead of stretching the tooltip across the pane.
const COMMENT_TOOLTIP_STYLE = { ...TOOLTIP_STYLE, maxWidth: 280 };

// The design-system "Selectable Items" checkbox (docs/design-system.md): a gold
// accent box + CheckIconAlt when checked, a dash when indeterminate, an empty
// bordered square otherwise — matching MultiSelectRow's picker. A visually
// hidden native input keeps full checkbox semantics (role, checked,
// indeterminate, keyboard) while the styled box supplies the gold visual.
// Shared by the row cells and the header select-all control.
export const SelectCheckbox = ({
  checked,
  indeterminate = false,
  disabled = false,
  onChange,
  'aria-label': ariaLabel,
}: {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onChange: () => void;
  'aria-label': string;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  // `indeterminate` is a DOM property, not an attribute — set it imperatively.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <CheckboxRoot $disabled={disabled}>
      <HiddenCheckbox
        ref={ref}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-label={ariaLabel}
      />
      <VisualCheck $active={checked || indeterminate} aria-hidden>
        {checked ? <CheckIconAlt /> : indeterminate ? <Dash /> : null}
      </VisualCheck>
    </CheckboxRoot>
  );
};

// Accessible but visually hidden — keeps role/checked/indeterminate/keyboard.
const HiddenCheckbox = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: 0;
  padding: 0;
  opacity: 0;
  pointer-events: none;
`;

const VisualCheck = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  border-radius: 3px;
  border: 1.5px solid ${({ $active }) => ($active ? 'var(--accent)' : 'var(--border)')};
  background: ${({ $active }) => ($active ? 'var(--accent)' : 'transparent')};
  transition:
    background-color var(--transition-fast),
    border-color var(--transition-fast);
`;

// Indeterminate mark for the header select-all when only some rows are picked.
const Dash = styled.span`
  width: 7px;
  height: 2px;
  border-radius: 1px;
  background: var(--background);
`;

const CheckboxRoot = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Nudge down so the box's bottom sits on the text baseline of the row's
     first line (and the header control a touch lower). */
  position: relative;
  top: 2px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  ${({ $disabled }) => $disabled && 'opacity: 0.5;'}

  /* The native input is visually hidden, so surface keyboard focus on the box. */
  ${HiddenCheckbox}:focus-visible + ${VisualCheck} {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

// The row `<tr>` — used directly as TableVirtuoso's `TableRow` component, so it
// carries the row + cell styling and the `data-selected` background.
export const EntryTr = styled.tr`
  border-bottom: 1px solid var(--border);

  &:hover [data-row-actions] {
    opacity: 1;
  }

  &[data-selected] {
    background: color-mix(in srgb, var(--primary) 8%, transparent);
  }

  > td {
    padding: 8px 12px;
    font-size: var(--text-xs);
    color: var(--foreground);
    vertical-align: middle;
  }

  /* Narrow leading checkbox column: centered under the header control and
     aligned to the row's (vertically-centered) first text line, not the bottom
     of taller multi-line rows. */
  .select {
    width: 1%;
    text-align: center;
    vertical-align: middle;
    white-space: nowrap;
  }

  /* Only the (potentially long) term columns break inside words; short cells
     wrap at spaces so language names don't fragment letter-by-letter. */
  .term,
  .translation {
    font-weight: var(--font-weight-medium);
    color: var(--foreground);
    overflow-wrap: anywhere;
  }

  /* Language columns are secondary context — smaller and grey so the terms
     stand out. */
  .lang {
    font-size: var(--text-xs);
    color: var(--text-secondary);
  }

  .meta .date {
    font-size: var(--text-xs);
    color: var(--text-muted);
    white-space: nowrap;
  }

  /* Collection provenance (All-translations view) — small, muted context. */
  .collection {
    font-size: var(--text-xs);
    color: var(--text-muted);
    overflow-wrap: anywhere;
  }

  .actions {
    width: 1%;
    white-space: nowrap;
  }
`;

const Actions = styled.div.attrs({ 'data-row-actions': true })`
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  transition: opacity var(--transition-fast);
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: none;
  border: none;
  border-radius: var(--radius-input);
  cursor: pointer;
  color: var(--text-secondary);

  &:hover {
    background: var(--border);
    color: var(--foreground);
  }
`;

const TranslationCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > .value {
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;

// Fixed-width leading gutter that holds the comment icon (or empty space) so
// the translation text starts at the same x across every row.
const CommentSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
`;

// A comment indicator, shown only when the entry has a note; hover reveals it.
const CommentButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: none;
  border: none;
  cursor: help;
  color: var(--text-secondary);

  &:hover {
    color: var(--accent);
  }
`;

const DntBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--accent);
  background: color-mix(in srgb, var(--accent) 14%, transparent);
  /* Keep the pill on one line — "Do not / translate" breaking mid-phrase in a
     narrow cell reads as broken; the parent cell wraps around it instead. */
  white-space: nowrap;
`;

const MetaStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
`;

const OriginBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: var(--text-xs);
  color: var(--text-secondary);
  background: var(--surface-elevated);
  white-space: nowrap;
`;

const ConfirmCell = styled.td`
  padding: 10px 12px;
`;

const ConfirmBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--destructive);
  border-radius: var(--radius-input);

  > p {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--text-secondary);
  }
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: var(--text-xs);
  color: var(--destructive);
`;
